/* eslint-disable no-duplicate-imports */
/**
 * Virtual Keyboard Directive
 * For terminals that doesn't open native keyboard on selecting input
 */
import {
  Directive,
  ElementRef,
  Input,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import Picker from 'pickerjs';
// #TODO replace jquery here
import $ from 'jquery';
import 'virtual-keyboard';
import type { KeyboardOptions } from 'virtual-keyboard';

import { DateTimeInputType } from '@app/shared/types/enums/date-time-input-type.enum';
import { DateFormatMask } from '@app/shared/types/enums/date-format-mask.enum';

@Directive({
  selector: '[virtualKeyboard]',
  standalone: false,
})
export class VirtualKeyboardDirective implements AfterViewInit, OnDestroy {
  @Input() public isVirtualKeyboardShown = true;

  @Input() public virtualKeyboardOptions: KeyboardOptions = { type: 'text' };

  @Input() public dateTimePickerOptions: Picker.Options;

  private dateTimePickerInstance: Picker;

  private defaultDateTimePickerOptions: Picker.Options = {
    headers: true,
    controls: true,
  };

  private defaultKeyboardOptions: KeyboardOptions = {
    type: 'text',
    layout: 'qwerty',
    accepted: (event: Event | string, keyboard: Element, element: Element) =>
      element.dispatchEvent(new Event('input')),
    hidden: (_event?: Event | string, keyboard?: any, _el?: Element) => {
      if (keyboard?.$keyboard) {
        if (keyboard.$keyboard.builtLayouts) {
          keyboard.$keyboard.builtLayouts = {};
        }
      }
      document.querySelector('.ui-keyboard-overlay')?.remove();
    },
  };

  public constructor(private el: ElementRef) {}

  public ngAfterViewInit(): void {
    if (!this.isVirtualKeyboardShown) {
      return;
    }

    const nativeElement: HTMLInputElement = this.el.nativeElement;

    if (this.isDateTimeField(nativeElement)) {
      this.initDateTimePicker(nativeElement);
      return;
    }

    this.initKeyboard(nativeElement);
  }

  public ngOnDestroy(): void {
    const $element = $(this.el.nativeElement);

    if (!$element) {
      return;
    }

    const keyboard = $element.data('keyboard');

    if (keyboard) {
      keyboard.destroy();
    }

    if (this.dateTimePickerInstance) {
      this.dateTimePickerInstance.destroy();
    }
  }

  private initDateTimePicker(nativeElement: HTMLInputElement): void {
    const options = {
      ...this.dateTimePickerOptions,
      ...this.defaultDateTimePickerOptions,
      format: this.defineDateTimeFormat(nativeElement.type),
      hide: () => {
        nativeElement.dispatchEvent(new Event('input'));
      },
    };

    nativeElement.type = 'text';
    this.dateTimePickerInstance = new Picker(nativeElement, options);
  }

  private initKeyboard(nativeElement: HTMLInputElement): void {
    const keyboardOptions: KeyboardOptions = {
      ...this.defaultKeyboardOptions,
      ...this.virtualKeyboardOptions,
    };

    $(nativeElement).keyboard(keyboardOptions);
  }

  private defineDateTimeFormat(fieldType: string): string {
    switch (fieldType) {
      case DateTimeInputType.DATETIME_LOCAL:
        return DateFormatMask.ISO_DATETIME_LOCAL;
      case DateTimeInputType.DATE:
        return DateFormatMask.ISO_DATE;
      case DateTimeInputType.TIME:
        return DateFormatMask.ISO_TIME;
      default:
        return DateFormatMask.ISO_DATETIME_LOCAL;
    }
  }

  private isDateTimeField(el: HTMLInputElement): boolean {
    return (
      el.type === DateTimeInputType.DATETIME_LOCAL ||
      el.type === DateTimeInputType.DATE ||
      el.type === DateTimeInputType.TIME
    );
  }
}
